/// <amd-module name="typeahead/features/CurrentLocationSuggester" />
/// <reference path="../../typings/typeahead/IPageLoadHandler.d.ts"/>
import {ClientHandlerRegistry} from "typeahead/client/ClientHandlerRegistry";
import {Experiment} from "typeahead/core/Experiment";
import {GeolocationDataProvider} from "typeahead/core/geolocation/GeolocationDataProvider";
import {GeolocationViewController} from "typeahead/core/geolocation/GeolocationViewController";
import {GeolocationMetricLogger} from "typeahead/core/geolocation/logging/GeolocationMetricLogger";
import {IDataProviderResponse} from "typeahead/core/IDataProvider";
import {IExperimentalFeature} from "typeahead/core/IExperimentalFeature";
import {IFocusSuggester} from "typeahead/core/IFocusSuggester";
import {ISuggesterResponse} from "typeahead/core/ISuggester";
import {FormLoadedMessage} from "typeahead/core/message/FormLoadedMessage";
import {TypeaheadMessage} from "typeahead/core/message/TypeaheadMessage";
import {prefetchInstance} from "typeahead/core/plugins/CompositePageLoadHandler";
import * as ExecutionHelper from "typeahead/core/plugins/ExecutionHelper";
import {IActiveSearch} from "typeahead/core/plugins/PrefetchLoader";
import * as StripingConfiguration from "typeahead/core/StripingConfiguration";
import {IStripingConfiguration} from "typeahead/core/StripingConfiguration";
import {SuggesterId} from "typeahead/core/SuggestionsAggregator";
import {TypeaheadUtils} from "typeahead/core/TypeaheadUtils";

const configuration: IStripingConfiguration[] = [{
        deviceTypes: ["Tablet", "Mobile"],
        tabs: ["hotel"],
    },
    {
        clients: ["Homepage", "AWSHomepage"],
        deviceTypes: ["Desktop"],
        tabs: ["activity"],
    },
    {
        clients: ["LXLandingPage"],
        deviceTypes: ["Desktop"],
    },
];

const activeSearchConfiguration: IStripingConfiguration[] = [
    {
        deviceTypes: ["Tablet", "Mobile"],
        experiment: {
            id: 15389,
            name: "uTA_PrepopulateCurrentLocationForMobile",
            value: 1,
        },
        tabs: ["hotel"],
    },
];

const lxCurrentLocationConfig: IStripingConfiguration[] = [
    {
        clients: ["Homepage", "AWSHomepage"],
        deviceTypes: ["Tablet", "Mobile"],
        experiment: {
            id: 25348,
            name: "ess_Current_Location_for_LX_Follow_up",
            value: 1,
        },
        tabs: ["activity"],
    },
    {
        clients: ["LXLandingPage"],
        deviceTypes: ["Tablet", "Mobile"],
        experiment: {
            id: 25348,
            name: "ess_Current_Location_for_LX_Follow_up",
            value: 1,
        },
    },
];

const allConfigs = [configuration, activeSearchConfiguration, lxCurrentLocationConfig];

const applicableClients: string[] = [
                                        "Homepage",
                                        "AWSHomepage",
                                        "HotelsLandingPage",
                                        "FlightsLandingPage",
                                        "PackageLandingPage",
                                        "LXLandingPage",
                                    ];

export class CurrentLocationSuggester implements IPageLoadHandler, IFocusSuggester, IExperimentalFeature {

    // When showing popular destinations, there can be 2 steps of typeahead suggestions
    private static isFirstStepInMultiStepTypeahead(): boolean {
        return window.location.search.indexOf("&depth=") < 0;
    }

    private geolocationDataProvider: GeolocationDataProvider;
    private geolocationViewController: GeolocationViewController;
    private geolocationMetricLogger: GeolocationMetricLogger;

    public handlePageLoad(options: any, context: IContext): void {
        if ( applicableClients.indexOf(context.clientId) > -1) {
            TypeaheadMessage.subscribe(FormLoadedMessage.getTopic(), (topic: string, message: FormLoadedMessage) => {
                ExecutionHelper.abTest.getBucketingPromise().done(() => {
                    context.tab = message.getTab();
                    if (TypeaheadUtils.isWithinMobileBreakpoint()
                        && (ExecutionHelper.shouldExecute(context, [].concat(configuration, lxCurrentLocationConfig))
                            || ExecutionHelper.shouldExecute(context, lxCurrentLocationConfig))) {
                        this.initialize();
                        if (this.geolocationViewController) {
                            this.geolocationViewControllerPageloadHandler(context);
                        }
                    }
                });
            });
        }
    }

    // whenever showOnFocusSuggestions() is triggered from core uta.js
    public suggestOnFocus(options: any, context: IContext): JQueryPromise<ISuggesterResponse> {
        return this.suggest(options, context);
    }

    // Current location suggest is now called whenever onfocus suggestions are enabled
    public suggest(options: any, context: IContext): JQueryPromise<ISuggesterResponse> {
        let deferred: JQueryDeferred<ISuggesterResponse> = $.Deferred();
        let emptySuggesterResponse: ISuggesterResponse = {id: this.getId(), results: []};
        if (    ( ExecutionHelper.shouldExecute(context, configuration) ||
                     ExecutionHelper.shouldExecute(context, lxCurrentLocationConfig)
            )
                && CurrentLocationSuggester.isFirstStepInMultiStepTypeahead()) {
            this.initialize();
            this.geolocationDataProvider.fetchData(options).done((response: IDataProviderResponse) => {
                deferred.resolve({id: this.getId(), results: response.results});
            }).fail(() => {
                deferred.resolve(emptySuggesterResponse);
            });
        } else {
            deferred.resolve(emptySuggesterResponse);
        }
        return deferred.promise();
    }

    public getId(): SuggesterId {
        return SuggesterId.CurrentLocationSuggester;
    }

    public getExperiments(): Experiment[] {
        return StripingConfiguration.getExperiments([].concat.apply([], allConfigs));
    }

    private initialize(): void {
        if (!this.geolocationDataProvider) {
            this.geolocationDataProvider = new GeolocationDataProvider();
            this.geolocationViewController =
                ClientHandlerRegistry.getGeolocationViewController(this.geolocationDataProvider);
            this.geolocationMetricLogger = new GeolocationMetricLogger();
        }
    }

    private geolocationViewControllerPageloadHandler(context: IContext) {
        prefetchInstance.getPrefetchActiveSearch()
            .done((activeSearchData: IActiveSearch) => {

                let isActiveSearch: boolean;
                let $inputElement: JQuery =
                    $(`form[data-lob-form=` + context.tab + `]`).find("input[name=destination]");
                let isInputPrepopulated: boolean = $($inputElement).val() ? true : false;

                let tab: string = (context.tab).toUpperCase();

                if (activeSearchData.hasOwnProperty(tab)) {
                    isActiveSearch = activeSearchData[tab];
                } else {
                    isActiveSearch = false ;
                }

                // If the user is not actively searching and input is prepopulated,
                // make explicit request to populate the currentLocationText
                if (!isActiveSearch && isInputPrepopulated && TypeaheadUtils.isWithinMobileBreakpoint()) {
                    // make explicit request to populate the currentLocationText and log
                    this.mustPopulateCurrentLocationConfigHelper(true, context, activeSearchConfiguration);

                } else {
                    this.handlePageLoadPrefetchHelper(context);
                }
            })
            .fail((data: IActiveSearch) => {
                this.handlePageLoadPrefetchHelper(context);
            });

    }

    private mustPopulateCurrentLocationConfigHelper
        (choice: boolean, context: IContext, config: IStripingConfiguration[]): void {
            if ( (config && ExecutionHelper.shouldExecute(context, config)) || config === null) {
                this.geolocationViewController.handlePageLoad(context, choice);
            }
    }

    private handlePageLoadPrefetchHelper(context: IContext): void {
        if (TypeaheadUtils.isWithinMobileBreakpoint()) {
            this.mustPopulateCurrentLocationConfigHelper(false, context, lxCurrentLocationConfig);
        } else {
            // Default current location behavior request
            this.mustPopulateCurrentLocationConfigHelper(false, context, null);
        }
    }
}
